import { useQuery } from '@tanstack/react-query';
import {
	Table,
	TableRow,
	TableHeader,
	TableHead,
	TableCell,
	TableBody
} from 'features/components/Table';
import { makeRequest } from 'features/entry-form-v2/data/makeRequest';
import { useGetNamesFromUserIdsQuery } from 'features/entry-form-v2/data/useGetNamesFromUserIdsQuery';
import { useLanguage } from 'features/hooks/useLanguage';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { LanguageType } from 'types';
import { CodelistHistoryExport } from './CodelistHistoryExport';

export const CodelistHistoryTab = () => {
	const { projectId } = useParams();
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const getCodelistHistoryQuery = useGetCodelistHistory(projectId as string);

	const userIdSet = useMemo(
		() => new Set(getCodelistHistoryQuery.data?.events.map(event => event.userId)),
		[getCodelistHistoryQuery.data]
	);

	const getNamesFromUserIdsQuery = useGetNamesFromUserIdsQuery({
		userIds: Array.from(userIdSet)
	});

	const isLoading = getCodelistHistoryQuery.isLoading || getNamesFromUserIdsQuery.isLoading;

	if (isLoading) {
		return <div>{translation.loading}</div>;
	}

	return (
		<div className="flex flex-col gap-4 overflow-y-auto max-w-[1000px]">
			<div className="flex justify-between items-center">
				<h3 className="text-lg font-semibold">{translation.historyTitle}</h3>

				<CodelistHistoryExport projectId={projectId as string} userIdSet={userIdSet} />
			</div>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{translation.createdAt}</TableHead>
						<TableHead>{translation.action}</TableHead>
						<TableHead>{translation.user}</TableHead>
					</TableRow>
				</TableHeader>

				<TableBody>
					{getCodelistHistoryQuery.data?.events.map(event => (
						<TableRow key={event.id}>
							<TableCell>
								{DateTime.fromISO(event.createdAt).toLocaleString(
									DateTime.DATETIME_MED_WITH_SECONDS
								)}
							</TableCell>
							<TableCell>{translation[event.eventType]}</TableCell>
							<TableCell>
								{getNamesFromUserIdsQuery.data?.namesFromUserIds[event.userId]}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
};

import { EVENT_TYPE_DICTIONARY } from './event-type-dictionary';

const DICTIONARY = {
	[LanguageType.English]: {
		createdAt: 'Timestamp',
		user: 'User',
		action: 'Action',
		loading: 'Loading...',
		historyTitle: 'Codelist History',

		...EVENT_TYPE_DICTIONARY[LanguageType.English]
	},
	[LanguageType.Norwegian]: {
		createdAt: 'Tidspunkt',
		user: 'Bruker',
		action: 'Hendelse',
		loading: 'Laster...',
		historyTitle: 'Kodelistehistorikk',

		...EVENT_TYPE_DICTIONARY[LanguageType.Norwegian]
	}
};

const useGetCodelistHistory = (projectId: string) => {
	return useQuery({
		queryKey: ['codelist-audit-log', projectId],
		queryFn: () =>
			makeRequest<GetCodelistAuditLogResponse>({
				method: 'getCodelistAuditLog',
				service: 'cde',
				data: {
					projectId,
					offset: 0,
					limit: 100
				}
			})
	});
};

type EntityType = 'RECORD';
type EventType =
	| 'CREATE_RECORD'
	| 'DELETE_CODELIST'
	| 'DELETE_RECORDS'
	| 'EXPORT_RECORDS'
	| 'GET_RECORD'
	| 'UPDATE_RECORD'
	| 'SEARCH_RECORDS'
	| 'IMPORT_BULK_RECORDS'
	| 'PARTIAL_IMPORT_RECORDS'
	| 'GET_RECORD_HISTORY'
	| 'GET_RECORD_ACCESS_HISTORY';

type AuditEvent = {
	createdAt: string;
	entityType: EntityType;
	eventType: EventType;
	id: string;
	projectId: string;
	userId: string;
	metadata?: string;
};
type GetCodelistAuditLogResponse = {
	events: AuditEvent[];
};
